import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'
import { Button, IconButton, ListItem, Tooltip, Typography } from '@mui/material'
import { Can } from 'config/ability'
import { handleLogout } from 'utils/handleLogout'
import { useSidebar } from 'hooks/use-sidebar'
import { SidebarUser } from './sidebar-user'
import { SidebarTheme } from './sidebar-theme'
import { SidebarLanguage } from './sidebar-language'
import * as Styled from './sidebar.styled'

import { IconLogOut01, IconMenu02 } from 'assets/icons'
import logoPlej from 'assets/images/logo_plej.png'
import logoBrand from 'assets/images/logo_brand.png'
import { sidebarNavItems } from './sidebar.data'

const Sidebar = (): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { isExpand, userData, toogleSidebar } = useSidebar()

  return (
    <Styled.Drawer as="header" variant="permanent" anchor="left" open={isExpand}>
      <Styled.Heading>
        <Styled.Logo className="SidebarLogo" src={logoBrand} alt="" />
        <Typography component="span" sx={visuallyHidden}>
          {process.env.REACT_APP_BRAND}
        </Typography>
      </Styled.Heading>

      <IconButton
        sx={{ display: 'flex', width: '100%', maxWidth: '44px' }}
        onClick={toogleSidebar}
        aria-label={isExpand ? 'Close menu' : 'Open menu'}
      >
        {IconMenu02}
      </IconButton>

      <nav>
        <Styled.List disablePadding>
          {sidebarNavItems.map(item => {
            const button = (
              <Styled.ListItemButton
                className="SidebarListItemButton"
                onClick={() => {
                  navigate(item.href)
                }}
                selected={location.pathname.startsWith(item.href)}
                disableGutters
              >
                <Styled.ListItemIcon>{item.icon}</Styled.ListItemIcon>

                <Typography
                  component="span"
                  sx={{ whiteSpace: 'nowrap' }}
                  fontWeight={600}
                  fontSize="16px"
                  lineHeight="1.5"
                >
                  {t(item.name)}
                </Typography>
              </Styled.ListItemButton>
            )

            return (
              <Can key={item.id} I="read" a={item.readSubject}>
                <ListItem sx={{ display: 'block' }} disablePadding disableGutters>
                  {isExpand ? (
                    button
                  ) : (
                    <Tooltip placement="right" title={t(item.name)}>
                      {button}
                    </Tooltip>
                  )}
                </ListItem>
              </Can>
            )
          })}
        </Styled.List>
      </nav>

      <Styled.WrapperSettings className="SidebarSettings">
        <SidebarLanguage />
        <SidebarTheme />
        {!isExpand && <SidebarUser />}
      </Styled.WrapperSettings>

      <Styled.WrapperUser className="SidebarUser">
        <Button
          component={Link}
          to="/account"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            position: 'absolute',
            inset: 0,
            textTransform: 'none',
          }}
        >
          <Typography
            component="span"
            display="block"
            fontWeight={600}
            fontSize="14px"
            lineHeight="20px"
            color={theme.palette.grey[900]}
          >
            {userData.name}
          </Typography>
          <Typography
            component="span"
            display="block"
            fontWeight={400}
            fontSize="14px"
            lineHeight="20px"
            color={theme.palette.grey[600]}
          >
            {userData.email}
          </Typography>
        </Button>

        <Tooltip title="Logout">
          <Styled.ButtonLogout onClick={handleLogout} aria-label="Logout">
            {IconLogOut01}
          </Styled.ButtonLogout>
        </Tooltip>
      </Styled.WrapperUser>

      <Styled.Footer component="footer" className="SidebarFooter">
        <Typography fontSize="12px" lineHeight="20px" color={theme.palette.grey[600]}>
          Made by
        </Typography>
        <Styled.LogoPlej src={logoPlej} alt="Plej" />
        <Typography fontSize="12px" lineHeight="20px" color={theme.palette.grey[600]}>
          © 2025
        </Typography>
      </Styled.Footer>
    </Styled.Drawer>
  )
}

export { Sidebar }
